<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { closesOnBack } from '@/lib/backCloses.js'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useUi } from '@/stores/ui.js'
import { ENTITIES } from '@/lib/entities.js'
import { entityLabel } from '@/lib/labels.js'
import { labelOf } from '@/lib/quickCreate.js'
import UiButton from '@/components/ui/UiButton.vue'
import RecordForm from '@/components/form/RecordForm.vue'
import RecordPanel from './RecordPanel.vue'
import { channelWarning } from '@/lib/channelWarning.js'

/**
 * Panels sliding in from the right, one over another: create panels (Dynamics' quick create) —
 * a new opportunity's panel can open one for its missing account, and that one for a contact,
 * each going back into the field it was opened from — and record panels, an existing record
 * opened from a reference (its form, what it shows under it, its time line), its full page a
 * click away. Nothing underneath is lost. A panel never closes on a click beside it; closing
 * one the person typed into asks first. Going to another page closes them all.
 */
const ui = useUi()
const router = useRouter()
// Going to another page closes them; a navigation stopped (Back closing the top one) does not.
router.afterEach((to, from, failure) => {
  if (!failure) ui.closeAllPanels()
})
const labels = ref({})
const { t } = useI18n()
const forms = ref({})
const saving = ref({})

const stack = computed(() => ui.creates)
const top = computed(() => stack.value[stack.value.length - 1] || null)
// Back on a phone closes the top panel (asking first if it was typed into), not the page.
let release = null
watch(() => stack.value.length > 0, (any) => {
  release?.()
  release = any ? closesOnBack(() => top.value && close(top.value)) : null
}, { immediate: true })
onBeforeUnmount(() => release?.())

async function close(entry) {
  if (forms.value[entry.id]?.changed && !(await ui.ask({ title: t('create.discardTitle'), message: t('create.discardMessage'), confirmLabel: t('create.discard'), danger: true }))) return
  ui.closeCreate(entry.id)
}

// A record panel's full page: the panels give way to it.
function openPage(entry) {
  router.push({ name: 'record', params: { entityPath: ENTITIES[entry.entity].path, uuid: entry.uuid } })
}

async function save(entry) {
  saving.value = { ...saving.value, [entry.id]: true }
  try {
    await forms.value[entry.id]?.submit()
  } finally {
    saving.value = { ...saving.value, [entry.id]: false }
  }
}

function saved(entry, record, result) {
  const created = { ...record, entity: entry.entity, label: labelOf(entry.entity, record) }
  if (entry.entity === 'opportunity' && result?.status === 'saved' && created.uuid) channelWarning(created.uuid, ui, t)
  ui.closeCreate(entry.id)
  if (entry.onSaved) {
    entry.onSaved(created, result)
    if (result?.status === 'saved') ui.success(t('create.createdInto', { label: created.label || entityLabel(entry.entity) }))
    return
  }
  if (result?.status !== 'saved') return
  const path = ENTITIES[entry.entity]?.path
  ui.toast(t('create.created', { label: created.label || entityLabel(entry.entity) }), {
    kind: 'success',
    timeout: 8000,
    action: path ? { label: t('create.open'), run: () => router.push(entry.entity === 'offer' ? { name: 'offer-editor', params: { uuid: created.uuid } } : { name: 'record', params: { entityPath: path, uuid: created.uuid } }) } : null,
  })
}

/** The panel's form on a page of its own, as typed so far (only a panel nothing waits for). */
function expand(entry) {
  const draft = forms.value[entry.id]?.snapshot() || entry.defaults
  try {
    sessionStorage.setItem(`addcrm:draft:${entry.entity}`, JSON.stringify(draft))
  } catch {
    // without storage the full page opens empty
  }
  ui.closeCreate(entry.id)
  router.push({ name: 'record-new', params: { entityPath: ENTITIES[entry.entity].path }, query: { draft: '1' } })
}

// Keys act on the top panel only: Escape closes it, Ctrl+S / Ctrl+Enter saves it.
function onKey(e) {
  const entry = top.value
  if (!entry || ui.confirm) return
  // A dialog opened over the panels (an acceptance, a fee change) takes its keys first.
  if (document.querySelectorAll('[role="dialog"][aria-modal="true"]').length > stack.value.length) return
  if (e.key === 'Escape') {
    e.preventDefault()
    e.stopImmediatePropagation()
    close(entry)
  } else if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'Enter')) {
    e.preventDefault()
    save(entry)
  }
}
onMounted(() => document.addEventListener('keydown', onKey, true))
onBeforeUnmount(() => document.removeEventListener('keydown', onKey, true))
</script>

<template>
  <Teleport to="body">
    <TransitionGroup enter-active-class="transition duration-200" enter-from-class="opacity-0 translate-x-8" leave-active-class="transition duration-150" leave-to-class="opacity-0 translate-x-8">
      <div v-for="(entry, i) in stack" :key="entry.id" class="fixed inset-0 flex justify-end bg-slate-900/25" :style="{ zIndex: entry.layer?.z ?? 41 + i }">
        <aside role="dialog" aria-modal="true" :aria-label="entry.kind === 'record' ? labels[entry.id] || entityLabel(entry.entity) : entityLabel(entry.entity, 'new')" :data-testid="entry.kind === 'record' ? `record-panel-${entry.entity}-${entry.uuid}` : `create-panel-${entry.entity}`"
          :class="['surface flex h-full w-full flex-col shadow-2xl', entry.kind === 'record' ? (i === 0 ? 'max-w-4xl' : 'max-w-3xl') : i === 0 ? 'max-w-2xl' : 'max-w-xl']">
          <header class="flex items-center gap-3 border-b border-default px-4 py-3">
            <i :class="['fa-solid fa-fw text-lg', ENTITIES[entry.entity]?.icon, ENTITIES[entry.entity]?.color]" aria-hidden="true"></i>
            <div v-if="entry.kind === 'record'" class="min-w-0 flex-1">
              <p class="text-2xs font-semibold uppercase tracking-wide text-faint">{{ entityLabel(entry.entity) }}</p>
              <h2 class="truncate text-base font-semibold" :data-testid="`record-panel-title`">{{ labels[entry.id] || '…' }}</h2>
            </div>
            <div v-else class="min-w-0 flex-1">
              <h2 class="truncate text-base font-semibold">{{ entityLabel(entry.entity, 'new') }}</h2>
              <p v-if="entry.from" class="truncate text-xs text-muted">{{ $t('create.for', { from: entry.from }) }}</p>
            </div>
            <button v-if="entry.kind === 'record'" type="button" class="rounded-md p-1.5 text-faint hover:bg-[var(--surface-3)]" :title="$t('create.fullPage')" :aria-label="$t('create.fullPage')" data-testid="record-panel-page" @click="openPage(entry)">
              <i class="fa-solid fa-up-right-and-down-left-from-center" aria-hidden="true"></i>
            </button>
            <button v-else-if="!entry.onSaved" type="button" class="rounded-md p-1.5 text-faint hover:bg-[var(--surface-3)]" :title="$t('create.fullPage')" :aria-label="$t('create.fullPage')" data-testid="create-expand" @click="expand(entry)">
              <i class="fa-solid fa-up-right-and-down-left-from-center" aria-hidden="true"></i>
            </button>
            <button type="button" class="rounded-md p-1.5 text-faint hover:bg-[var(--surface-3)]" :aria-label="$t('common.close')" @click="close(entry)">
              <i class="fa-solid fa-xmark" aria-hidden="true"></i>
            </button>
          </header>
          <div class="min-h-0 flex-1 overflow-y-auto p-4">
            <RecordPanel v-if="entry.kind === 'record'" :ref="(el) => (forms[entry.id] = el)" :entity="entry.entity" :uuid="entry.uuid" @loaded="(r) => (labels = { ...labels, [entry.id]: r.label || r.number || '' })" />
            <RecordForm v-else :ref="(el) => (forms[entry.id] = el)" :entity="entry.entity" :defaults="entry.defaults" hide-actions quiet-success @saved="(record, result) => saved(entry, record, result)" />
          </div>
          <footer class="flex items-center gap-2 border-t border-default px-4 pt-3 safe-bottom-pad">
            <span class="hidden flex-1 text-xs text-faint sm:block">{{ $t('create.keys') }}</span>
            <span class="flex-1 sm:hidden"></span>
            <UiButton @click="close(entry)">{{ entry.kind === 'record' ? $t('common.close') : $t('common.cancel') }}</UiButton>
            <UiButton v-if="entry.kind === 'record'" variant="primary" icon="fa-floppy-disk" :loading="saving[entry.id]" :disabled="!forms[entry.id]?.changed" data-testid="record-panel-save" @click="save(entry)">{{ $t('common.save') }}</UiButton>
            <UiButton v-else variant="primary" icon="fa-check" :loading="saving[entry.id]" data-testid="create-save" @click="save(entry)">{{ $t('create.saveClose') }}</UiButton>
          </footer>
        </aside>
      </div>
    </TransitionGroup>
  </Teleport>
</template>
